import React, { useState, useEffect, useCallback } from "react";
import { toast } from "../../components/AppToast";
import { createAdjustment } from "../../services/adjustment/adjustment";
import CommonDateInput from "../../components/CommonDateInput";

const emptyHeader = {
    Warehouse: "",
    ItrnKey: "",
    TranType: "AJ",
    Adjustmentkey: "",
    StorerKey: "",
    ReferenceNumber: "",
    AdjustmentAddDate: new Date().toISOString(),
    Notes: "",
    SourceType: "",
    SourceKey: "",
};

const emptyDetail = {
    Sku: "",
    Description: "",
    Location: "",
    Lot: "",
    LPN: "",
    Quantity: 0,
    ReasonCode: "",
    PackKey: "STD",
    UOM: "EA",
    Notes: "",
    Status: "OK",
    NetWeight: 0,
    Lottable01: "",
    Lottable02: "",
    Lottable09: "",
    Lottable10: "",
    CountSequence: 0,
};

const headerFieldOrder = [
    "Warehouse",
    "ItrnKey",
    "TranType",
    "Adjustmentkey",
    "StorerKey",
    "ReferenceNumber",
    "AdjustmentAddDate",
    "Notes",
    "SourceType",
    "SourceKey",
];

const detailFieldOrder = [
    "Sku",
    "Description",
    "Location",
    "Lot",
    "LPN",
    "Quantity",
    "ReasonCode",
    "PackKey",
    "UOM",
    "Notes",
    "Status",
    "NetWeight",
    "Lottable01",
    "Lottable02",
    "Lottable09",
    "Lottable10",
    "CountSequence",
];

export default function CreateAdjustment({ onCancel, onSubmit }) {
    const [header, setHeader] = useState(emptyHeader);
    const [details, setDetails] = useState([{ ...emptyDetail }]);
    const [loading, setLoading] = useState(false);

    const handleHeaderChange = useCallback((e) => {
        const { name, value } = e.target;
        setHeader((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleDetailChange = useCallback((idx, e) => {
        const { name, value } = e.target;
        setDetails((prev) => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], [name]: value };
            return updated;
        });
    }, []);

    const addDetailRow = () => {
        setDetails((prev) => [...prev, { ...emptyDetail }]);
    };

    const removeDetailRow = (idx) => {
        setDetails((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleSubmit = async () => {
        if (!header.Warehouse || !header.StorerKey) {
            toast.error("Warehouse and Storer Key are required");
            return;
        }

        if (details.length === 0 || !details[0].Sku) {
            toast.error("At least one detail line with SKU is required");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                AdjHeader: {
                    ...header,
                    ReferenceNumber: header.ReferenceNumber
                        ? Number(header.ReferenceNumber)
                        : 0,
                },
                AdjDetails: details.map((d, idx) => ({
                    ...d,
                    Adjustmentlinenumber: String(idx + 1).padStart(4, "0"),
                    Quantity: Number(d.Quantity) || 0,
                    NetWeight: Number(d.NetWeight) || 0,
                    CountSequence: Number(d.CountSequence) || 0,
                })),
            };

            await createAdjustment(payload);
            toast.success("Adjustment created successfully");
            onSubmit?.();
        } catch (e) {
            toast.error(typeof e === "string" ? e : "Failed to create adjustment");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container">
            <div className="mb-3 border-bottom pb-2 d-flex gap-4 text-primary fw-semibold">
                <span className="text-muted">Adjustment Header</span>
            </div>

            <div className="row gx-2">
                {headerFieldOrder.map((key) => (
                    <div className="col-md-2" key={key}>
                        <div className="form-group mb-3">
                            <label className="form-label">
                                {key.replace(/([A-Z])/g, " $1").trim()}
                            </label>
                            {key === "AdjustmentAddDate" ? (
                                <CommonDateInput
                                    name={key}
                                    value={header[key]}
                                    showTime
                                    onChange={handleHeaderChange}
                                />
                            ) : (
                                <input
                                    className="form-control"
                                    name={key}
                                    value={header[key]}
                                    onChange={handleHeaderChange}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mb-3 mt-4 border-bottom pb-2 d-flex justify-content-between align-items-center">
                <span className="text-muted fw-semibold">
                    Adjustment Details
                </span>
                <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={addDetailRow}
                >
                    + Add Line
                </button>
            </div>

            {details.map((detail, idx) => (
                <div key={idx} className="border rounded p-3 mb-3 bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-semibold text-muted">
                            Line {idx + 1}
                        </span>
                        {details.length > 1 && (
                            <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeDetailRow(idx)}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                    <div className="row gx-2">
                        {detailFieldOrder.map((key) => (
                            <div className="col-md-2" key={key}>
                                <div className="form-group mb-3">
                                    <label className="form-label">
                                        {key.replace(/([A-Z])/g, " $1").trim()}
                                    </label>
                                    <input
                                        className="form-control"
                                        name={key}
                                        value={detail[key]}
                                        onChange={(e) =>
                                            handleDetailChange(idx, e)
                                        }
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            <div className="text-center mt-4">
                <button
                    className="btn btn-secondary me-2"
                    onClick={onCancel}
                >
                    Back
                </button>
                <button
                    className="btn btn-primary"
                    disabled={loading}
                    onClick={handleSubmit}
                >
                    {loading ? "Saving..." : "Create Adjustment"}
                </button>
            </div>
        </div>
    );
}
